import { ETextAnchor, IParagraphEntry, svgToHtmlIgnoredAttrs, svgToHtmlMapping } from "./text-editor-common";
import { cmykToRgb } from '@/common/colours';
import { convertToNum } from '@/common/units';
import { kebabCaseToCamelCase } from '@/common/pdUtils';
import { NS } from "@/common/namespaces";
import { IElementMetadata, ITextFrameCharacterMetadata, metadataServiceInstance } from "@/services";

export function svgToHtml(textElement: SVGTextElement, strict: boolean = false) {
    const paragraphs: IParagraphEntry[] = [];
    const textBoxMetadata = metadataServiceInstance.getMetadataForElement(textElement, strict);
    let anchor: ETextAnchor;

    if (textBoxMetadata) {
        anchor = extractTextFromMetadata(textElement, textBoxMetadata, paragraphs);
    } else {
        anchor = extractTextFromRawSvg(textElement, paragraphs);
    }


    let textBoxBBox: { width: number; } = textBoxMetadata?.element_positional_info;
    
    if(!textBoxBBox || paragraphs.length === 1) {
        textBoxBBox = textElement.getBBox();
    }

    const textBoxWidth = textBoxBBox.width;
    let xShiftForAnchorCorrection: number;
    switch(anchor) {
        case ETextAnchor.Left:
            xShiftForAnchorCorrection = 0;
            break;

        case ETextAnchor.Middle:
            xShiftForAnchorCorrection = textBoxWidth / 2;
            break;

        case ETextAnchor.Right:
            xShiftForAnchorCorrection = textBoxWidth;
            break;
    }


    let left = 0;
    for (const para of paragraphs) {
        left = Math.min(left, para.x ?? 0);
    }

    // The left edge can be negative or positive depending on how the <text> element is output.
    // This applies an extra shift to correct positioning further.
    xShiftForAnchorCorrection += left;

    const ret = {
        text: paragraphs.map(p => `<p style="line-height:${p.lineHeight}; direction:${p.direction};">${p.text}</p>`).join(" "),
        anchor,
        xShiftForAnchorCorrection,
    };

    return ret;
}

function extractTextFromMetadata(textElement: SVGTextElement, elementMetadata: IElementMetadata, paragraphs: IParagraphEntry[]) {
    if (!elementMetadata?.paragraph_metadata) {
        return extractTextFromRawSvg(textElement, paragraphs);
    }

    let anchor = ETextAnchor.Left;
    let nextY = 0;
    for (const para of elementMetadata.paragraph_metadata) {
        let currentParagraph: IParagraphEntry = {
            text: "",
            x: null,
            width: 0,
            y: nextY,
            lineHeight: 1.2
        };
        let maxLeading = 0;
        let largestFontSize = 0;

        switch (para.ParagraphJustification) {
            case 0:
                anchor = ETextAnchor.Left;
                break;
            case 1:
                anchor = ETextAnchor.Right;
                break;
            case 2:
                anchor = ETextAnchor.Middle;
                break;
        }

        for (const characterMetadata of elementMetadata.character_metadata) {
            if (characterMetadata.rangeEnd < para.rangeStart || characterMetadata.rangeStart > para.rangeEnd) {
                continue;
            }

            const rangeStart = Math.max(characterMetadata.rangeStart, para.rangeStart);
            const rangeEnd = Math.min(characterMetadata.rangeEnd, para.rangeEnd);
            const substrStart = rangeStart - para.rangeStart;
            const substrEnd = substrStart + (rangeEnd - rangeStart);

            const span = document.createElement("span");
            span.textContent = para.content.substring(substrStart, substrEnd);
            span.style.color = getRgbFillColourFromMetadata(characterMetadata);
            span.style.fontSize = `${characterMetadata.FontSize}px`;
            span.style.fontFamily = characterMetadata.Font;
            span.style.letterSpacing = `${(characterMetadata.Tracking / 1000.0)}em`;

            largestFontSize = Math.max(largestFontSize, characterMetadata.FontSize);
            maxLeading = Math.max(maxLeading, characterMetadata.Leading);

            currentParagraph.text += span.outerHTML;
        }

        currentParagraph.lineHeight = maxLeading / largestFontSize;
        paragraphs.push(currentParagraph);
        nextY = currentParagraph.y + largestFontSize * currentParagraph.lineHeight;
    }

    return anchor;
}

function extractTextFromRawSvg(textElement: SVGTextElement,  paragraphs: IParagraphEntry[]) {
    const paragraphDirection = textElement.getAttribute("direction") ?? undefined;

    let currentParagraph: IParagraphEntry = {
        text: "",
        x: null,
        width: 0,
        y: 0,
        lineHeight: 1.2,
        direction: paragraphDirection
    };
    let largestFontSize = -1;

    for (const child of textElement.childNodes) {
        if (child.nodeType !== Node.ELEMENT_NODE && child.nodeType !== Node.TEXT_NODE) {
            continue;
        }

        let childEl: SVGTSpanElement | null = null;

        // Pretend we have a <tspan> if we have a text node.
        if (child.nodeType === Node.TEXT_NODE) {
            childEl = document.createElementNS(NS.SVG, "tspan");
            childEl.textContent = child.textContent;
        } else {
            childEl = child as SVGTSpanElement;
        }

        if (childEl.tagName.toLowerCase() !== "tspan") {
            console.warn(`Unexpected child element ${childEl.tagName} found in <text> element. Ignoring.`);
            continue;
        }

        if (childEl.hasAttribute("font-size")) {
            const newFontSize = convertToNum("", childEl.getAttribute("font-size") ?? -1);
            largestFontSize = Math.max(newFontSize, largestFontSize);
        }

        let addNewParagraph = true;
        if (childEl.hasAttribute("y") || childEl.hasAttribute("dy")) {
            const lastY = currentParagraph?.y ?? 0;
            let newY: number;

            if (childEl.hasAttribute("dy")) {
                const dY = convertToNum("", childEl.getAttribute("dy") ?? 0);

                if (dY === 0) {
                    addNewParagraph = false;
                } else {
                    newY = lastY + dY;
                }
            } else {
                newY = convertToNum("", childEl.getAttribute("y") ?? 0);

                if (Math.abs(lastY - newY) < Number.EPSILON) {
                    addNewParagraph = false;
                }
            }

            if (addNewParagraph) {
                let lineHeight: number = 1.2;
                if (largestFontSize > 0) {
                    lineHeight = (newY - lastY) / largestFontSize;
                }

                currentParagraph.lineHeight = lineHeight;
                paragraphs.push(currentParagraph);
                currentParagraph = {
                    text: "",
                    x: null,
                    width: 0,
                    y: newY,
                    lineHeight: 1.2,
                    direction: paragraphDirection
                };

                largestFontSize = -1;
            }
        }

        const boundingRect = childEl.getBBox();
        currentParagraph.width += boundingRect.width;

        if (!currentParagraph.x) {
            currentParagraph.x = boundingRect.x;
        }

        const span = document.createElement("span");
        span.textContent = childEl.textContent;

        for (const parent of [textElement, childEl]) {
            for (const attr of parent.getAttributeNames()) {
                if (svgToHtmlIgnoredAttrs.some(a => a === attr)) {
                    continue;
                }

                const val = parent.getAttribute(attr);
                const attrLower = attr.toLowerCase();
                let targetAttr: string;

                if (attrLower in svgToHtmlMapping) {
                    targetAttr = svgToHtmlMapping[attrLower];
                } else {
                    targetAttr = kebabCaseToCamelCase(attr);
                }

                if (span.style[targetAttr] !== undefined) {
                    span.style[targetAttr] = val;
                } else {
                    span.setAttribute(attr, val);
                }
            }
        }

        currentParagraph.text += span.outerHTML;
    }

    if (currentParagraph.text.length > 0) {
        paragraphs.push(currentParagraph);
    }

    paragraphs.sort((a, b) => a.y - b.y);

    let anchor = ETextAnchor.Left;
    let left = 0;
    let right = 0;
    const allRightEdges: number[] = [];
    for (const para of paragraphs) {
        para.right = (para.x ?? 0) + para.width;
        allRightEdges.push(para.right);

        left = Math.min(left, para.x ?? 0);
        right = Math.max(right, para.right);
        if (Math.abs(para.x) > 0.0001) {
            anchor = ETextAnchor.Middle;
        }
    }

    const allRightEdgesEqual = allRightEdges.every((val, i, arr) => Math.abs(val - arr[0]) < 0.5);
    if (allRightEdgesEqual) {
        anchor = ETextAnchor.Right;
    }

    return anchor;
}

function getRgbFillColourFromMetadata(metadata: ITextFrameCharacterMetadata) {
    const fillColour = metadata.FillColor ?? metadata.FillColour;
    if(!fillColour) {
        return "rgb(0,0,0)";
    }

    if('c' in fillColour.colourHint && 'm' in fillColour.colourHint && 'y' in fillColour.colourHint && 'k' in fillColour.colourHint) {
        const rgb = cmykToRgb(fillColour.colourHint); 
        return `rgb(${rgb.r},${rgb.g},${rgb.b})`;
    }

    return "rgb(0,0,0)";
}
